/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'primereact/tag';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useDebounce } from '../../hooks/useDebounce';
import { CATEGORY_OPTIONS, CHANNEL_OPTIONS } from '../../components/notifications/notification-filter-options';
import { notificationTypesService, type NotificationType } from '../../services/notification-types.service';
import { totalOf, NEWEST_FIRST, sortParams, type SortState } from '../../services/notification-engine.types';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { extractErrorMessage } from '../../utils/error-handler';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { ProjectCell } from '../../components/common/project-cell';
import { useAccessibleProjects } from '../../hooks/useProjectScope';
import ProjectFilter from '../../components/common/project-filter';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import Icon from '../../components/common/icon';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import ChannelIcons from '../../components/common/channel-icons';

/** A deactivated type delivers nothing — that is the only state there is. */
const NotificationTypeList = ({ embedded = false }: { embedded?: boolean }) => {
  const { navigateToCreate, navigateToView, navigateToEdit } = useSecureNavigation({
    entity: 'notification type', basePath: '/notification-types',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.NOTIFICATION_TYPE);
  const canUpdate = useCanUpdate(JOB_NAMES.NOTIFICATION_TYPE);
  const canDelete = useCanDelete(JOB_NAMES.NOTIFICATION_TYPE);

  // Which project each row belongs to. The COLUMN always shows — "this project"
  // versus the shared "Global" rows is worth seeing even to someone with one
  // project — while the filter beside it only appears when there is a choice.
  const { projects, canScopeByProject } = useAccessibleProjects();

  const [rows, setRows] = useState<NotificationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  // Server-side sort: the table asks, the API orders. Sorting in the browser
  // would only order the rows already fetched, which is not what a sort control
  // appears to promise. Newest first until the viewer picks a column.
  const [sort, setSort] = useState<SortState>(NEWEST_FIRST);
  const [toDelete, setToDelete] = useState<NotificationType | null>(null);

  // Filters. Search is debounced so a keystroke does not fire a request; the two
  // dropdowns apply immediately. All three are server-side — they narrow the
  // whole table, not only the rows already on screen.
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [channel, setChannel] = useState<string | null>(null);
  const [appId, setAppId] = useState<string | null>(null);
  const [showCore, setShowCore] = useState(false);
  const debouncedSearch = useDebounce(search, 400);

  const showError = useCallback((m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 8000 }), []);
  const showOk = useCallback((m: string) => toast.current?.show({ severity: 'success', summary: 'Success', detail: m, life: 3000 }), []);

  // The engine pages on skip/limit rather than page/pageSize, so the table's
  // `first` index is passed straight through instead of being divided out.
  const fetchRows = useCallback(async (skip = 0, limit = pageSize, sorting: SortState = sort) => {
    try {
      setLoading(true);
      const resp = await notificationTypesService.list({
        skip, limit, ...sortParams(sorting),
        ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        ...(category ? { category } : {}),
        ...(channel ? { channel } : {}),
        ...(appId ? { appId } : {}),
        include_global: showCore,
      });
      setRows(resp.data || []);
      setTotal(totalOf(resp));
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to load notification types'));
    } finally {
      setLoading(false);
    }
  }, [pageSize, sort, showError, debouncedSearch, category, channel, appId, showCore]);

  // First load, and again on any filter change — always from page one, so a
  // narrowed result cannot strand the viewer on a page that no longer exists.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFirst(0); fetchRows(0, pageSize); }, [debouncedSearch, category, channel, appId, showCore]);

  const handlePage = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(e.first, e.rows); };

  const handleSort = (e: any) => {
    const next: SortState = { field: e.sortField || null, order: e.sortOrder || null };
    setSort(next);
    setFirst(0);
    fetchRows(0, pageSize, next);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await notificationTypesService.softDelete(toDelete._id);
      setToDelete(null);
      showOk('Notification type deleted');
      fetchRows(first, pageSize);
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to delete notification type'));
    }
  };

  const columns: SaasColumn<NotificationType>[] = useMemo(() => [
    { field: 'notificationCode', header: 'Code', sortable: true, body: (r) => <span className="font-mono text-xs">{r.notificationCode}</span> },
    { field: 'category', header: 'Category', sortable: true, body: (r) => <Tag value={r.category} severity="secondary" className="text-xs px-1.5 py-0.5" /> },
    { field: 'priority', header: 'Priority', sortable: true, body: (r) => <span className="text-xs">{r.priority || '-'}</span> },
    {
      field: 'channels', header: 'Channels',
      body: (r) => <ChannelIcons channels={r.channels} />,
    },
    {
      field: 'appId', header: 'Project', style: { minWidth: 150 },
      body: (r: NotificationType) => <ProjectCell appId={r.appId} projects={projects} />,
    },
    { field: 'createdAt', header: 'Created', sortable: true, body: (r) => <span className="text-xs text-gray-500 whitespace-nowrap">{r.createdAt ? formatTimestamp(r.createdAt) : '-'}</span> },
    {
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => (
        <div className="flex gap-1 justify-center">
          <button aria-label="View" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToView(r._id)} title="View">
            <Icon name="eye" size={17} className="text-green-600 dark:text-green-400" />
          </button>
          {canUpdate && <button aria-label="Edit" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToEdit(r._id)} title="Edit">
            <Icon name="pencil" size={17} className="text-blue-600 dark:text-blue-400" />
          </button>}
          {canDelete && <button aria-label="Delete" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setToDelete(r)} title="Delete">
            <Icon name="trash" size={17} className="text-red-600 dark:text-red-400" />
          </button>}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canUpdate, canDelete, canScopeByProject, projects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.NOTIFICATION_TYPE}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-NotificationType-List">
        {!embedded && (
          <PageHeader icon="pi pi-tags" title="Notification Types" subtitle="The class a notification belongs to, and the delivery policy it inherits" testId="NotificationType.Header" />
        )}
        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="notification-type-master" columns={columns} data={rows} loading={loading} dataKey="_id"
            emptyMessage="No notification types found" first={first} rows={pageSize} totalRecords={total}
            onPage={handlePage} rowsPerPageOptions={[5, 10, 25, 50]} sortField={sort.field || undefined} sortOrder={sort.order} onSort={handleSort} onFilter={() => {}} testId="NotificationTypeList"
            globalSearch={search} onGlobalSearchChange={setSearch} globalSearchPlaceholder="Search code or description..."
            toolbarStart={
              <div className="flex flex-wrap items-center gap-2">
                <ProjectFilter value={appId} onChange={setAppId} testId="NotificationTypeList.Filter.Project" />
                <Dropdown value={category} options={CATEGORY_OPTIONS} onChange={(e: any) => setCategory(e.value || null)} placeholder="Category" showClear style={{ minWidth: '150px' }} data-testid="NotificationTypeList.Filter.Category" />
                <Dropdown value={channel} options={CHANNEL_OPTIONS} onChange={(e: any) => setChannel(e.value || null)} placeholder="Channel" showClear style={{ minWidth: '150px' }} data-testid="NotificationTypeList.Filter.Channel" />
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="NotificationTypeList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={<>
              <span className="text-sm text-gray-400 whitespace-nowrap">{total} {total === 1 ? 'record' : 'records'}</span>
              <RefreshButton onClick={() => fetchRows(first, pageSize)} loading={loading} testId="NotificationTypeList.Refresh" />
              {canCreate && <Button label="Create Type" icon="pi pi-plus" onClick={() => navigateToCreate()} size="small" />}
            </>}
          />
        </div>
      </div>
      <DeleteConfirmDialog visible={!!toDelete} onCancel={() => setToDelete(null)} onDelete={handleDelete}
        message={`Delete notification type '${toDelete?.notificationCode}'?`} testId="NotificationTypeList.Delete" />
    </PermissionGuard>
  );
};

export default NotificationTypeList;
